import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import { PRACTICE_AREAS, FAQS } from "@/data/content";

export default function PracticeAreas() {
    const { hash } = useLocation();

    useEffect(() => {
        if (hash) {
            const id = hash.slice(1);
            const el = document.getElementById(id);
            if (el) {
                setTimeout(
                    () =>
                        el.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        }),
                    100,
                );
            }
        }
    }, [hash]);

    const schema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: PRACTICE_AREAS.map((p, i) => ({
            "@type": "Service",
            position: i + 1,
            name: p.title,
            description: p.lede,
            areaServed: ["Sacramento County, CA", "Placer County, CA"],
            provider: {
                "@type": "Attorney",
                name: "Joseph G. Helfrick",
            },
        })),
    };

    return (
        <>
            <SEO
                title="Personal Injury Practice Areas — Sacramento, CA, and surrounding areas"
                description="Practice areas: car and truck accidents, slip and fall, medical and dental malpractice, dog bites and animal attacks, workplace accidents, ski accidents, government tort claims, and property disputes. Serving Sacramento, CA, and surrounding areas."
                path="/practice-areas"
                schema={schema}
                faqs={FAQS}
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Practice Areas", path: "/practice-areas" },
                ]}
            />

            {/* HEADER */}
            <section className="border-b border-[#1F3458]" data-testid="pa-hero">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-8">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                            Practice Areas
                        </div>
                        <h1 className="mt-6 font-serif text-5xl lg:text-7xl text-[#F7F5EB] leading-[0.95] font-light">
                            The cases we{" "}
                            <span className="italic text-[#D4AF37]">take</span>.
                            <br />
                            The cases we win.
                        </h1>
                        <p className="mt-8 text-lg text-[#9FB3D4] max-w-3xl leading-relaxed">
                            A focused personal-injury practice serving
                            Sacramento, CA, and surrounding areas. Below are
                            the six categories of cases we handle most often —
                            tap any one to read what we look for, what to
                            expect, and how we build the file.
                        </p>
                        <div
                            className="tldr mt-8 max-w-3xl border-l-2 border-[#D4AF37] pl-5 text-[#F7F5EB]/85 text-sm leading-relaxed"
                            data-speakable
                        >
                            <span className="block text-[#D4AF37] text-[10px] uppercase tracking-[0.28em] font-bold mb-2">
                                Quick Answer
                            </span>
                            Joe Helfrick handles six categories of personal
                            injury cases in Sacramento, CA, and surrounding areas: (1)
                            car and truck accidents, (2) slip, trip and fall,
                            (3) medical and dental malpractice, (4) dog bites
                            and animal attacks, (5) workplace accidents, and
                            (6) other claims including ski accidents,
                            government tort claims, and property disputes. All
                            cases are taken on a contingency fee — no fee
                            unless we win.
                        </div>
                    </div>
                </div>
            </section>

            {/* AREAS */}
            <section className="border-b border-[#1F3458]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 lg:py-20">
                    {PRACTICE_AREAS.map((p, i) => (
                        <article
                            key={p.slug}
                            id={p.slug}
                            data-testid={`pa-${p.slug}`}
                            className={`grid lg:grid-cols-12 gap-10 py-14 lg:py-20 ${
                                i !== 0
                                    ? "border-t border-[#1F3458]"
                                    : ""
                            }`}
                        >
                            <div className="lg:col-span-5">
                                <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                                    0{i + 1}
                                </div>
                                <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-[#F7F5EB] leading-tight">
                                    {p.title}
                                </h2>
                                <p className="mt-5 text-lg text-[#D4AF37]/90 italic">
                                    {p.lede}
                                </p>
                            </div>
                            <div className="lg:col-span-7">
                                {p.body.split("\n\n").map((para, idx) => (
                                    <p
                                        key={idx}
                                        className="text-[#F7F5EB]/85 text-base lg:text-lg leading-relaxed mb-5"
                                    >
                                        {para}
                                    </p>
                                ))}
                                <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                                    {p.bullets.map((b) => (
                                        <li
                                            key={b}
                                            className="flex items-start gap-3 text-sm text-[#9FB3D4]"
                                        >
                                            <Check
                                                size={16}
                                                className="text-[#D4AF37] mt-1 shrink-0"
                                            />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/contact"
                                    data-testid={`pa-cta-${p.slug}`}
                                    className="mt-8 inline-flex items-center gap-2 text-[#D4AF37] font-semibold border-b border-[#D4AF37]/40 hover:border-[#D4AF37] pb-1 transition-colors"
                                >
                                    Discuss a {p.title.toLowerCase()} case{" "}
                                    <ArrowRight size={14} />
                                </Link>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            {/* FAQ */}
            <section
                data-testid="pa-faq"
                className="border-b border-[#1F3458] bg-[#0B1A36]"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                            Frequently Asked
                        </div>
                        <h2 className="mt-4 font-serif text-4xl text-[#F7F5EB] leading-tight">
                            Quick answers
                        </h2>
                    </div>
                    <div className="lg:col-span-8 divide-y divide-[#1F3458]">
                        {FAQS.map((f) => (
                            <details
                                key={f.q}
                                data-testid={`faq-${f.q.slice(0, 18).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="group py-6"
                            >
                                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                                    <span className="font-serif text-xl lg:text-2xl text-[#F7F5EB] leading-snug">
                                        {f.q}
                                    </span>
                                    <span className="text-[#D4AF37] text-2xl transition-transform group-open:rotate-45 leading-none">
                                        +
                                    </span>
                                </summary>
                                <p className="mt-4 text-[#9FB3D4] leading-relaxed">
                                    {f.a}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section data-testid="pa-bottom-cta">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <h2 className="font-serif text-4xl lg:text-6xl text-[#F7F5EB] leading-tight">
                            Recognize your situation?{" "}
                            <span className="italic text-[#D4AF37]">
                                Let's talk about it.
                            </span>
                        </h2>
                        <p className="mt-6 text-[#9FB3D4] text-lg max-w-2xl">
                            A free consultation with Joe — or sixty seconds on
                            the case evaluator if you'd rather start online.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex flex-wrap gap-4">
                        <Link
                            to="/contact"
                            data-testid="pa-bottom-cta-contact"
                            className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#B8962A] transition-colors"
                        >
                            Free Case Review <ArrowRight size={16} />
                        </Link>
                        <Link
                            to="/case-evaluator"
                            data-testid="pa-cta-evaluator"
                            className="inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#D4AF37] hover:text-[#07142B] transition-colors"
                        >
                            60-Second Case Evaluator
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
